'use client';

import { Fragment } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { getIsQuantaPage } from '@/components/layout/utils';
import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType } from '@/context/types';
import { useQuantaCheck } from '@/hooks/api/quanta/useQuantaCheck';
import { useQuantaLogin } from '@/hooks/api/quanta/useQuantaLogin';
import { cn } from '@/utils/cn';

import ConnectedWallet from './components/ConnectedWallet';
// import QuantaBg from './components/QuantaBg';
import PointsRankingCard from './components/PointsRankingCard';
import ProgressCard from './components/ProgressCard';
import QuantaMigrationPanel from './components/QuantaMigrationPanel';
import VaultOwnership from './components/VaultOwnership';
import { QuantaMigrationProvider } from './context/QuantaMigrationContext';
import useRedirect from './hooks/useRedirect';
import { getMaxPointsForLevel, LEVELS } from './utils';

interface QuantaLayoutProps {
  children: React.ReactNode;
}

const QuantaLayoutContent: React.FC<QuantaLayoutProps> = ({ children }: QuantaLayoutProps) => {
  const tLevels = useTranslations('quanta.levels');
  const pathname = usePathname();
  const { walletType } = useDucatClient();
  const { data: checkData } = useQuantaCheck();
  const login = useQuantaLogin({ enabled: !!checkData?.data.isRegistered });
  const loginData = login.data;

  useRedirect(!loginData);

  const totalPoints = loginData?.data.points_total ?? 0;
  const levelResponse = loginData?.data.user.level || 1;
  const levelsResponse = loginData?.data.levels ?? null;

  const maxPoints = getMaxPointsForLevel(levelResponse, levelsResponse);
  const isLevelUp = totalPoints >= maxPoints;

  return (
    <div data-theme-scope="quanta" className={cn(!!loginData && 'flex flex-1')}>
      {/* <QuantaBg isShown={!!loginData}> */}
      <div className={cn(!!loginData && 'flex flex-1 flex-col gap-4 p-4 3xl:p-8')}>
        <QuantaMigrationPanel loggedIn={login.isSuccess} userId={loginData?.data.user.user_id} authenticationPending={login.isFetching} />
        <Fragment key={loginData?.data.user.user_id}>
          {!getIsQuantaPage(pathname) && !!loginData && (
            <div className={cn('flex justify-between gap-4', isLevelUp ? 'items-start' : 'items-center')}>
              <PointsRankingCard totalPoints={totalPoints} ranking={loginData.data.ranking} width={300} />
              <ProgressCard
                isLevelUp={isLevelUp}
                status={LEVELS[levelResponse]?.titleKey ? tLevels(LEVELS[levelResponse].titleKey) : ''}
                level={levelResponse}
                totalPoints={totalPoints}
                maxPoints={maxPoints}
              />
            </div>
          )}
          {loginData && <ConnectedWallet userId={loginData.data.user.user_id} />}
          {loginData && walletType === WalletType.Metamask && <VaultOwnership userId={loginData.data.user.user_id} />}
          {children}
        </Fragment>
      </div>
      {/* </QuantaBg> */}
    </div>
  );
};

export default function QuantaLayout({ children }: QuantaLayoutProps) {
  return (
    <QuantaMigrationProvider>
      <QuantaLayoutContent>{children}</QuantaLayoutContent>
    </QuantaMigrationProvider>
  );
}
